import { useSupplierSuggestions } from '@bridebook/web/app-basic/lib/hooks/use-supplier-suggestions';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import {
  GetSuggestionValue,
  ShouldRenderSuggestions,
  SuggestionsFetchRequested,
} from 'react-autosuggest';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { SuggestionField } from '@bridebook/ui';
import { IBaseSuggestion } from '@bridebook/ui/src/components/bbcommon/inputs/suggestion-field-v2/suggestion-field-v2';
import { useReviewCallbacks } from '@bridebook/web/app-shared/lib/reviews/hooks/use-review-callbacks';

interface IProps {
  searchTerm: string;
  onSuggestionSelect: (supplier: ISupplier) => void;
  onSuggestionsFetchRequest: (value: string) => void;
}

export const SupplierSuggestions = ({
  onSuggestionSelect,
  onSuggestionsFetchRequest,
  searchTerm,
}: IProps) => {
  const { t } = useTranslation();
  const shouldRenderSuggestions: ShouldRenderSuggestions = (value) => value.length > 0;
  const { onCustomClick, onSuggestionSelected } = useReviewCallbacks();
  const onSuggestionSelectedCb = onSuggestionSelected(onSuggestionSelect);

  // Autosuggest helper function for getting option value from suggestion object
  const getSuggestionValue: GetSuggestionValue<IBaseSuggestion> = useCallback(
    (suggestionObj) => {
      if (suggestionObj.custom) return t('search:suggestions.custom');
      return suggestionObj.name || '';
    },
    [t],
  );

  // Sets search term for requested suppliers
  const onSuggestionsFetchRequested = useCallback<SuggestionsFetchRequested>(
    ({ value }) => {
      onSuggestionsFetchRequest(value);
    },
    [onSuggestionsFetchRequest],
  );

  // Supplier suggestions query
  const { supplierSuggestions } = useSupplierSuggestions({
    searchTerm,
    size: 5,
  });

  // Supplier suggestions with custom suggestion record
  const supplierSuggestionsWithCustom = useMemo(
    () => [...(supplierSuggestions || []), { custom: true }],
    [supplierSuggestions],
  );

  return (
    <SuggestionField
      placeholder={t('supplier:review.page.suggestion.placeholder')}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      getSuggestionValue={getSuggestionValue}
      shouldRenderSuggestions={shouldRenderSuggestions}
      suggestions={supplierSuggestionsWithCustom}
      onSuggestionSelected={onSuggestionSelectedCb}
      onCustomClick={onCustomClick}
      supplierSuggest
      showCustomSuggestion
      customSuggestLabel={t('supplier:review.page.suggestion.label')}
      value={searchTerm}
    />
  );
};
